import React, { useState, useCallback } from "react";
import { Col, Stack, Row, Container, Badge, Button, Card, Toast, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GeoAlt, Clock, StarFill, Star, CheckCircleFill, ExclamationCircleFill } from "react-bootstrap-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  useStoreInfo, useStoreCatalog, useStoreRatingStats, useUserStoreRating,
} from "../lib/query/queries/useStoreQueries";
import { useSubmitStoreRating } from "../lib/query/mutations/useStoreMutations";
import { useUserContext } from "../context/UserContext";
import { useServiceAvailability } from "../context/ServiceAvailabilityContext";
import { toast } from "../utilities/toast";
import { Seo } from "../components/Seo";
import { Breadcrumb } from "../components/Breadcrumb";
import { generateLiquorStoreSEO, generateNoIndexSEO } from "../lib/seo/metaGenerators";

const StoreNameSkeleton = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="d-flex flex-column gap-2"
    >
      <div 
        className="rounded-3" 
        style={{ 
          width: "200px", 
          height: "24px", 
          backgroundColor: "#f0f0f0",
          animation: "shimmer 1.5s infinite",
          background: "linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)",
          backgroundSize: "200% 100%",
        }}
      />
      <div 
        className="rounded-3" 
        style={{ 
          width: "100px", 
          height: "20px", 
          backgroundColor: "#f0f0f0",
          animation: "shimmer 1.5s infinite",
          background: "linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)",
          backgroundSize: "200% 100%",
        }}
      />
    </motion.div>
  );
};

const RatingCard = ({name, storeId}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [error, setError] = useState(null);
  const { user, token } = useUserContext();
  const navigate = useNavigate();

  const { data: userRating, isLoading } = useUserStoreRating(storeId, user?.uid);
  const rating = userRating?.rating || 0;

  const { mutate: rate, isPending: isSubmitting } = useSubmitStoreRating({
    onSuccess: () => toast.success('Your rating has been recorded'),
    onError: (err) => setError(err.message || 'Failed to submit rating. Please try again.'),
  });

  const handleRating = useCallback((value) => {
    if (!user) {
      toast.error('Please login to rate this store');
      navigate('/login');
      return;
    }
    setError(null);
    rate({ storeId, rating: value, uid: user.uid, token });
  }, [storeId, user, token, navigate, rate]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-0 shadow-sm" style={{ 
        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '16px',
        overflow: 'hidden'
      }}>
        <Card.Body className="p-4">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex flex-column">
                <h6 className="mb-1 fw-semibold" style={{ color: '#2c3e50' }}>Rate {name}</h6>
                {!user && (
                  <motion.small 
                    className="text-muted"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Login to rate this store
                  </motion.small>
                )}
                {user && rating > 0 && (
                  <motion.small 
                    className="text-success d-flex align-items-center gap-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <CheckCircleFill size={14} />
                    You rated this store {rating} stars
                  </motion.small>
                )}
              </div>
            </div>
            
            <div className="d-flex align-items-center justify-content-center gap-2 py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <motion.div
                  key={star}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Button
                    variant="link"
                    className="p-0 position-relative"
                    onClick={() => handleRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    disabled={isSubmitting || !user || isLoading}
                    style={{
                      transition: 'all 0.2s ease-in-out',
                      transform: `scale(${isSubmitting ? 0.9 : 1})`
                    }}
                  >
                    {(hoverRating || rating) >= star ? (
                      <StarFill
                        size={24}
                        className="text-warning"
                        style={{ 
                          cursor: !user ? "not-allowed" : isSubmitting ? "not-allowed" : "pointer",
                          filter: "drop-shadow(0 2px 4px rgba(255, 193, 7, 0.3))",
                          opacity: !user ? 0.3 : isSubmitting ? 0.5 : 1,
                          transition: 'all 0.2s ease-in-out'
                        }}
                      />
                    ) : (
                      <Star
                        size={24}
                        className="text-warning"
                        style={{ 
                          cursor: !user ? "not-allowed" : isSubmitting ? "not-allowed" : "pointer",
                          opacity: !user ? 0.3 : isSubmitting ? 0.3 : 0.5,
                          transition: 'all 0.2s ease-in-out'
                        }}
                      />
                    )}
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3"
            >
              <Alert 
                variant="danger" 
                className="py-2 mb-0 border-0"
                style={{ 
                  background: 'rgba(220, 53, 69, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <div className="d-flex align-items-center">
                  <ExclamationCircleFill size={16} className="me-2" />
                  <small>{error}</small>
                </div>
              </Alert>
            </motion.div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

const LiquorStoreInfo = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { isOpen: isServiceOpen } = useServiceAvailability();

  const storeInfoQuery = useStoreInfo(storeId);
  const ratingStatsQuery = useStoreRatingStats(storeId);
  const catalogQuery = useStoreCatalog(storeId);

  const storeInfo = storeInfoQuery.data ?? {};
  const ratingStats = ratingStatsQuery.data ?? { average_rating: 0, total_no_ratings: 0 };
  const catalog = catalogQuery.data ?? [];
  const storeInfoLoading = storeInfoQuery.isLoading || ratingStatsQuery.isLoading || catalogQuery.isLoading;

  const seo = !storeInfoLoading && storeInfo.store_name
    ? generateLiquorStoreSEO(storeId, storeInfo, ratingStats)
    : generateNoIndexSEO("Liquor Store");

  const CATEGORY_IMAGE_FALLBACK = "https://via.placeholder.com/300x200?text=No+Image";

  const getCategoryImage = (type, subType) => {
    const match = catalog.find((product) => product.type === type && product.subType === subType);
    return match?.imageSrc || CATEGORY_IMAGE_FALLBACK;
  };

  const handleRollback = () => {
    navigate(-1);
  };

  const handleTileClick = (type, subType) => {
    navigate(`/stores/liquor/${storeId}/${type}/${subType}`);
  };

  const formatLocation = (loc) => {
    if (!loc) return "Location not available";
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      return `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`;
    }
    return "Invalid location";
  };


  const categories = [
    {
      title: 'Wines',
      items: [
        { name: 'Red wine', image: getCategoryImage('wine', 'red'), type: 'wine', subType: 'red' },
        { name: 'Rose wine', image: getCategoryImage('wine', 'rose'), type: 'wine', subType: 'rose' },
        { name: 'Sparkling wine', image: getCategoryImage('wine', 'sparkling'), type: 'wine', subType: 'sparkling' },
      ]
    },
    {
      title: 'Spirits',
      items: [
        { name: 'Scotch and Whisky', image: getCategoryImage('spirit', 'whisky'), type: 'spirit', subType: 'whisky' },
        { name: 'Gin and Vodka', image: getCategoryImage('spirit', 'gin'), type: 'spirit', subType: 'gin' },
        { name: 'Tequila and Rum', image: getCategoryImage('spirit', 'rum'), type: 'spirit', subType: 'rum' },
      ]
    },
    {
      title: 'Beers and Ciders',
      items: [
        { name: 'Beers and Ciders', image: getCategoryImage('beer', 'cider'), type: 'beer', subType: 'cider' },
      ]
    }
  ];

  return (
    <Container className="py-3 py-md-4 px-3 px-md-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Seo {...seo} />
        {!storeInfoLoading && <Breadcrumb items={seo.breadcrumbItems} />}
        <Stack gap={3} gap-md={4}>
          {/* Store Header */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 gap-md-4" id="store-info-header">
            <div className="d-flex flex-column gap-3 flex-grow-1">
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="outline-primary"
                  className="rounded-circle p-2"
                  onClick={handleRollback}
                  style={{
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderWidth: "2px",
                  }}
                >
                  <ArrowLeft size={20} />
                </Button>
                
                <div className="d-flex flex-column">
                  {storeInfoLoading ? (
                    <StoreNameSkeleton />
                  ) : (
                    <>
                      <h1 className="fs-4 fw-bold mb-1">{storeInfo.name}</h1>
                      <Badge 
                        bg="primary" 
                        className="align-self-start"
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "0.5rem",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        Liquor Store
                      </Badge>
                    </>
                  )}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-3">
                <div className="d-flex align-items-center text-muted">
                  <GeoAlt size={16} className="me-2 text-primary" />
                  <small className="text-truncate" style={{ maxWidth: "150px" }}>
                    {formatLocation(storeInfo.location)}
                  </small>
                </div>
                {/* Reflects the same Teco-wide service_availability config
                    that gates checkout, not this store's own hours. */}
                <div className="d-flex align-items-center text-muted">
                  <Clock
                    size={16}
                    className="me-2"
                    style={{ color: isServiceOpen ? "#0d6efd" : "#FF9500" }}
                  />
                  <small style={isServiceOpen ? undefined : { color: "#FF9500", fontWeight: 600 }}>
                    {isServiceOpen ? "Open now" : "Closed"}
                  </small>
                </div>
                <div className="d-flex align-items-center text-muted">
                  <StarFill size={16} className="me-2 text-warning" />
                  <small>
                    {storeInfoLoading ? (
                      <span className="placeholder-glow">
                        <span className="placeholder col-4"></span>
                      </span>
                    ) : (
                      `${ratingStats.average_rating.toFixed(1)} (${ratingStats.total_no_ratings})`
                    )}
                  </small>
                </div>
              </div>
            </div>

            <div style={{ maxWidth: "300px" }} id="store-info-rating-card">
              <RatingCard name={storeInfo.name} storeId={storeId} />
            </div>
          </div>

          {/* Categories Grid */}
          <Stack gap={4}>
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <h3 className="mb-3" style={{ color: '#2d3748' }}>{category.title}</h3>
                <Row xs={2} md={3} lg={4} className="g-3 g-md-4">
                  {category.items.map((item, itemIndex) => (
                    <Col key={itemIndex}>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card
                          className="border-0 shadow-sm h-100"
                          onClick={() => handleTileClick(item.type, item.subType)}
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <Card.Body className="d-flex flex-column align-items-center p-4">
                            <motion.img
                              src={item.image}
                              alt={item.name}
                              style={{
                                height: '120px',
                                objectFit: 'contain',
                                marginBottom: '16px',
                              }}
                              whileHover={{ scale: 1.05 }}
                              transition={{ duration: 0.2 }}
                              onError={(e) => {
                                e.target.src = CATEGORY_IMAGE_FALLBACK;
                                e.target.onerror = null;
                              }}
                            />
                            <h5 className="mb-0 text-center" style={{ color: '#2d3748' }}>
                              {item.name}
                            </h5>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            ))}
          </Stack>
        </Stack>
      </motion.div>
    </Container>
  );
};

export default LiquorStoreInfo;
